import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { parseLocaleNumber } from 'src/app/utils/format-currency';
import { LocaleService } from 'src/app/services/locale.service';
import { ApplicationService } from 'src/app/services/application-service';
import { handleApiError } from 'src/app/utils/error';
import { Subscription, timer } from 'rxjs';
import { FlowService } from 'src/app/services/flow.service';
import { TrackingService } from 'src/app/services/tracking.service';
import { ApplicationsApiService } from 'src/app/services/api/applications-api.service';
import { LeadApiService } from 'src/app/services/api/lead-api.service';
import { StorageService } from 'src/app/services/storage.service';
import { BasicInfoService } from 'src/app/services/basic-info.service';
import { UpdateLeadRequestModel } from 'src/app/models/lead.model';
import { Countries, Flows } from 'src/app/app.consts';
import { environment } from 'src/environments/local/environment';

@Component({
  selector: 'app-turnover',
  templateUrl: './turnover.component.html',
  styleUrls: ['./turnover.component.scss'],
})
export class TurnoverComponent implements OnInit, OnDestroy {
  applicationId!: number;
  turnoverForm!: FormGroup;
  error!: string;
  estimatedTurnoverCurrency!: any;
  locale!: string;
  country!: string;
  subs: Array<Subscription> = [];
  telemarketing!: boolean;
  @Input() isSummary!: boolean;

  constructor(
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private applicationService: ApplicationService,
    private localeService: LocaleService,
    public flowService: FlowService,
    private trackingService: TrackingService,
    private applicationsApiService: ApplicationsApiService,
    private leadApi: LeadApiService,
    private storageService: StorageService,
    private basicInfoService: BasicInfoService
  ) {
  }

  ngOnInit(): void {
    this.telemarketing = this.flowService.flowName === Flows.TM_SIGN_UP;

    this.applicationId = this.applicationService.applicationId;
    this.locale = this.localeService.locale;
    this.country = environment.country;

    const initialEstimatedTurnover = this.applicationService.estimatedTurnover;
    const initialEstimatedTurnoverPrev = this.applicationService.estimatedTurnoverPrev;

    this.turnoverForm = this.formBuilder.group({
      estimatedTurnover: [initialEstimatedTurnover, { 
        updateOn: 'blur', validators: [Validators.required] 
      }],
      estimatedTurnoverPrev: [initialEstimatedTurnoverPrev, {
        updateOn: 'blur', validators: [Validators.required] 
      }],
    });

    this.subs.push(
      this.estimatedTurnover.valueChanges
        .subscribe((v) => {
          if (this.checkTypeAndLength('string', v)) {
            v = parseLocaleNumber(v, this.locale);
            this.applicationService.estimatedTurnover = v;
            if (this.isSummary) {
              this.subs.push(
                this.applicationsApiService.updateApplication({
                  estimatedTurnover: v,
                  applicationId: this.applicationId,
                }).subscribe(() => {
                  this.error = '';
                  this.trackFields('turnoverUpdated');
                },
                  (error: any) => handleApiError(error, this.translateService)
                  )
              )
            } else {
              this.subs.push(
                this.leadApi.updateLead(this.updateLead(v, 'estimatedTurnover'))
                  .subscribe(() => {
                    this.error = '';
                    this.trackFields('turnover');
                  })
              );
            }
          }
        },
        (error: any) => handleApiError(error, this.translateService),
        )
    );

    this.subs.push(
      this.estimatedTurnoverPrev.valueChanges
        .subscribe((v) => {
          if (this.checkTypeAndLength('string', v)) {
            v = parseLocaleNumber(v, this.locale);
            this.applicationService.estimatedTurnoverPrev = v;
            if (this.isSummary) {
              this.subs.push(
                this.applicationsApiService.updateApplication({
                  estimatedTurnoverPrev: v,
                  applicationId: this.applicationId,
                }).subscribe(() => { 
                  this.error = '';
                  this.trackFields('turnoverPrevUpdated');
                },
                  (error: any) => handleApiError(error, this.translateService)
                  )
              )
            } else {
              this.subs.push(
                this.leadApi.updateLead(this.updateLead(v, 'estimatedTurnoverPrev'))
                  .subscribe(() => {
                    this.error = '';
                    this.trackFields('turnoverPrev');
                  })
              );
            }
          }
        },
        (error: any) => handleApiError(error, this.translateService),
        )
    );
  }

  ngOnDestroy(): void {
    this.subs.map((sub) => sub.unsubscribe());
  }

  handleButtonClick(): void {
    this.subs.push(timer(1000).subscribe(() => {
      if (!this.form.invalid) {
        this.flowService.nextScreen();
      }
    }));
  }

  updateLead(value: number, key: string): UpdateLeadRequestModel {
    const leadId = this.country === Countries.NETHERLANDS
      ? this.storageService.getLeadId() 
      : this.basicInfoService.leadId;

    return {
      id: leadId,
      email: this.basicInfoService.email,
      phoneNumber: this.basicInfoService.phoneNumber,
      [key]: value
    }
  }

  get form(): FormGroup {
    return this.turnoverForm;
  }

  get estimatedTurnover(): FormControl {
    return this.form.get('estimatedTurnover') as FormControl;
  }

  get estimatedTurnoverPrev(): FormControl {
    return this.form.get('estimatedTurnoverPrev') as FormControl;
  }

  trackFields(field: string): void {
    this.trackingService.filledField(field);
  }

  checkTypeAndLength(type: string, value: string): boolean {
    return typeof value === type && value.length ? true : false;
  }

  getErrors(control: string): string | null {
    const estimatedTurnover = this.estimatedTurnover;
    const estimatedTurnoverPrev = this.estimatedTurnoverPrev;

    switch (control) {
      case 'estimatedTurnover':
        if (estimatedTurnover.hasError('required')) {
          return this.translateService.instant('global.error.estimatedTurnoverRequired');
        }
        return null;
      case 'estimatedTurnoverPrev':
        if (estimatedTurnoverPrev.hasError('required')) {
          return this.translateService.instant('global.error.estimatedTurnoverPrevRequired');
        }
        return null;
      default:
        return null;
    }
  }
}
